import _ from "lodash";
import {
  FIRST_NAME,
  MIDDLE_NAME,
  LAST_NAME,
  FULL_NAME,
  NAME_MAPPING,
  DEFAULT_NAME_ORDER_PREFERENCE,
} from "Constants/stringConstants";
import {
  ENABLE_STUDENT_MIDDLE_NAME,
  ENABLE_USER_MIDDLE_NAME,
  STUDENTNAME_IDENTIFIER,
  STUDENT_NAME_ORDER,
  STUDENT_PRINT_NAME_ORDER,
} from "Constants/permissionConstants";
import { getCleanNameOption } from "./GetCleanNameOption";
import { parseConfigurationValue } from "./Common";
import ACLStore from "lib/aclStore";
import { getSettingValue } from "Utils/ConfigurationUtils";

const _getDefaultOrderBy = () => {
  const orderByConfig = ACLStore.getConfigObj("ShowNameOrderedBy");
  const orderBy = parseConfigurationValue(orderByConfig) || FIRST_NAME;

  return orderBy;
};

const _getStudentAdditionalInfoValue = ({ studentAdditionalInfos = [] }) => {
  const additionalFieldUidForNameValue = getSettingValue({
    name: "StudentAdditionalFieldUidForName",
  });

  const additionalInfo = _.find(
    studentAdditionalInfos,
    info => info.fieldUid === additionalFieldUidForNameValue
  );

  return _.get(additionalInfo, "value", "");
};

export const getShowStudentAdditionalFieldWithName = () => {
  return ACLStore.can("FeatureFlag:ShowStudentAdditionalFieldWithName");
};

const _getDefaultStudentNameOrderPreference = () => {
  const defaultStudentNameOrderPreference =
    getSettingValue({
      name: STUDENT_NAME_ORDER,
      readFromUserAndOrgLevel: true,
    }) || DEFAULT_NAME_ORDER_PREFERENCE;

  return defaultStudentNameOrderPreference;
};

const _getDefaultStudentIdentifierNamePreference = () => {
  const defaultStudentIdentifierNamePreference =
    getSettingValue({
      name: STUDENTNAME_IDENTIFIER,
      readFromUserAndOrgLevel: true,
    }) || FIRST_NAME;
  return defaultStudentIdentifierNamePreference;
};

export const getDefaultStudentNameOrderPreference = _.memoize(
  _getDefaultStudentNameOrderPreference,
  params => JSON.stringify(params)
);

const _getDefaultStudentPrintNameOrderPreference = () => {
  const defaultStudentPrintNameOrderPreference =
    getSettingValue({
      name: STUDENT_PRINT_NAME_ORDER,
      readFromUserAndOrgLevel: true,
    }) || DEFAULT_NAME_ORDER_PREFERENCE;

  return defaultStudentPrintNameOrderPreference;
};

const getDefaultStudentPrintNameOrderPreference = _.memoize(
  _getDefaultStudentPrintNameOrderPreference,
  params => JSON.stringify(params)
);

const getDefaultStudentIdentifierNamePreference = _.memoize(
  _getDefaultStudentIdentifierNamePreference,
  params => JSON.stringify(params)
);
export const getStudentAdditionalInfoValue = _.memoize(
  _getStudentAdditionalInfoValue,
  params => JSON.stringify(params)
);
export const getDefaultOrderBy = _.memoize(_getDefaultOrderBy, params =>
  JSON.stringify(params)
);

const getDefaultNameOrderPreferenceForStudent = shouldGeneratePrintOrder => {
  let defaultStudentNameOrderPreference = [];
  if (shouldGeneratePrintOrder) {
    defaultStudentNameOrderPreference = getDefaultStudentPrintNameOrderPreference();
  } else {
    defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference();
  }
  return defaultStudentNameOrderPreference;
};

export const getFormattedStudentNameV3 = ({
  student,
  shouldGeneratePrintOrder = false,
}) => {
  let fullName = "";
  const defaultStudentNameOrderPreference = getDefaultNameOrderPreferenceForStudent(
    shouldGeneratePrintOrder
  );

  defaultStudentNameOrderPreference.forEach(order => {
    const isInitial = _.includes(order, "_INITIAL");
    const cleanOrder = getCleanNameOption(order, true);
    const propertyName = NAME_MAPPING[cleanOrder];
    const propertyValue = _.get(student, propertyName, "");

    if (propertyValue) {
      if (fullName) {
        fullName += " ";
      }
      if (isInitial) {
        const cleanOrderWithInitial = getCleanNameOption(order);
        fullName += _.replace(
          order,
          cleanOrderWithInitial,
          propertyValue.charAt(0)
        );
      } else {
        fullName += _.replace(order, cleanOrder, propertyValue);
      }
    }
  });

  return fullName;
};

export const getFormattedStudentNameV2 = ({
  student,
  shouldGeneratePrintOrder = false,
  orderBy,
}) => {
  let fullName = "";
  let wordWithComma = true;

  const defaultStudentNameOrderPreference = getDefaultNameOrderPreferenceForStudent(
    shouldGeneratePrintOrder
  );

  const validValues = [FIRST_NAME, MIDDLE_NAME, LAST_NAME];
  const nameOrderPreference = _.filter(
    defaultStudentNameOrderPreference,
    orderPreference => {
      const result = _.includes(validValues, orderPreference);
      return result;
    }
  );

  const orderIndex = nameOrderPreference.indexOf(orderBy);

  if (orderIndex !== -1) {
    nameOrderPreference.splice(orderIndex, 1);
    nameOrderPreference.unshift(orderBy);
  }

  nameOrderPreference.forEach((order, index) => {
    const propertyName = NAME_MAPPING[order];
    let propertyValue = _.get(student, propertyName, "");

    // Condition for nameOrderPreference [MIDDLE_NAME, LAST_NAME]
    if (_.isEqual(order, MIDDLE_NAME) && nameOrderPreference.length < 3) {
      if (!propertyValue) propertyValue = _.get(student, "firstName", "");
      if (index === 0) wordWithComma = false;
    }

    if (index === 0 && order === "FIRST_NAME") wordWithComma = false;

    if (propertyValue) {
      if (index === 1 && fullName && wordWithComma) {
        fullName += ", ";
      } else if (fullName && index < nameOrderPreference.length) {
        fullName += " ";
      }
      fullName += propertyValue;
    }
  });

  return fullName;
};

export const getFormattedStudentName = ({
  student,
  showStudentUID = false,
  orderBy = null,
  showAdditionalInfo = false,
  t = null,
  shouldGeneratePrintOrder = false,
}) => {
  const isStudentUIDVisible = ACLStore.can("Common:StudentUID");
  const isAdditionalInfoVisible = getShowStudentAdditionalFieldWithName();

  let studentName;
  const {
    firstName,
    lastName,
    sourceId,
    studentAdditionalInfos = [],
    isBlocked = false,
  } = student || {};

  const enableUserMiddleName = ACLStore.can(ENABLE_USER_MIDDLE_NAME);
  const enableStudentMiddleName = ACLStore.can(ENABLE_STUDENT_MIDDLE_NAME);
  const showNameOrderedBy = getDefaultOrderBy();

  if (enableUserMiddleName) {
    studentName = getFormattedStudentNameV3({
      student,
      shouldGeneratePrintOrder,
    });
  } else if (enableStudentMiddleName) {
    studentName = getFormattedStudentNameV2({
      student,
      shouldGeneratePrintOrder,
      orderBy,
    });
  } else {
    const orderPreference = orderBy ?? showNameOrderedBy;
    studentName = _.isEqual(orderPreference, LAST_NAME)
      ? `${lastName}, ${firstName}`
      : `${firstName} ${lastName}`;
  }

  if (isAdditionalInfoVisible && showAdditionalInfo) {
    const additionalInfoValue = getStudentAdditionalInfoValue({
      studentAdditionalInfos,
    });

    if (!_.isEmpty(additionalInfoValue)) {
      studentName += ` (${additionalInfoValue})`;
    }
  } else if (isStudentUIDVisible && showStudentUID && sourceId) {
    studentName += ` (${sourceId})`;
  }

  if (isBlocked && t) {
    studentName = t("schoolSetup:blocked", {
      studentName,
    });
  }

  return studentName;
};

export const getStudentIdentifierName = ({
  student,
  shouldGeneratePrintOrder = false,
}) => {
  const defaultPropertyName = NAME_MAPPING[FIRST_NAME];
  const defaultValue = _.get(student, defaultPropertyName, "");

  if (ACLStore.can(ENABLE_USER_MIDDLE_NAME)) {
    return getFormattedStudentNameV3({ student, shouldGeneratePrintOrder });
  }

  const defaultStudentIdentifierNamePreference = getDefaultStudentIdentifierNamePreference();

  const propertyValue = NAME_MAPPING[defaultStudentIdentifierNamePreference];
  const studentIdentifier = _.get(student, propertyValue, "");

  return ACLStore.can(ENABLE_STUDENT_MIDDLE_NAME)
    ? studentIdentifier || defaultValue
    : defaultValue;
};

export const getStudentOrderBy = () => {
  const enableUserMiddleName = ACLStore.can(ENABLE_USER_MIDDLE_NAME);
  const enableStudentMiddleName = ACLStore.can(ENABLE_STUDENT_MIDDLE_NAME);
  if (enableUserMiddleName || enableStudentMiddleName) {
    return FULL_NAME;
  }

  return getDefaultOrderBy();
};

export const getCleanDefaultStudentNameOrderPreference = () => {
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference();
  return defaultStudentNameOrderPreference.map(order =>
    getCleanNameOption(order, true)
  );
};
